/**
 * Thread management hook
 * Handles thread switching, creation, and clearing
 */

import { useCallback, useRef } from 'react';
import { ThreadLoader } from './ThreadLoader';
import { usePlanMode } from '@/context/PlanModeContext';
import { useAgentConfig } from '@/context/AgentConfigContext';
import { useTodosOptional } from '@/context/TodoContext';
import type { ThreadMessage } from '@/types/messages';
import { logger } from '@/utils/logger';
import { getMessageState } from '@/api/threads.api';

interface UseThreadManagerProps {
  setCurrentThreadId: (threadId: string | null) => void;
  setMessages: (
    messages: ThreadMessage[] | ((prev: ThreadMessage[]) => ThreadMessage[])
  ) => void;
  setError: (error: string | null) => void;
  setAvailableIds: (
    ids: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
  streamingThreadIds: Set<string>;
}

export function useThreadManager({
  setCurrentThreadId,
  setMessages,
  setError,
  setAvailableIds,
  streamingThreadIds,
}: UseThreadManagerProps) {
  const threadLoaderRef = useRef<ThreadLoader | null>(null);
  if (!threadLoaderRef.current) {
    threadLoaderRef.current = new ThreadLoader();
  }
  const threadLoader = threadLoaderRef.current;
  const { extractAndSetPlan, clearPlan } = usePlanMode();
  const { resetPlanMode: resetAgentPlanMode } = useAgentConfig();
  const todosContext = useTodosOptional();

  // Monotonically increasing counter that guards against concurrent
  // switchToThread calls overwriting each other's state.  Each call
  // increments the counter; after every `await` it checks whether a
  // newer call has started and bails if so.
  const switchVersionRef = useRef(0);

  /**
   * Switch to a different thread
   */
  const switchToThread = useCallback(
    async (
      projectId: string,
      threadId: string,
      recoveryMessageId?: string,
    ) => {
      // If the target thread has an active SSE connection, its
      // messages are already in React state and are more
      // up-to-date than the backend.  Reloading from DB/Redis
      // would replace the live in-memory message (with its
      // generated ID) with a DB UUID message, causing the
      // still-running SSE to create a duplicate when it can no
      // longer find its original message by ID.
      if (streamingThreadIds.has(threadId)) {
        setCurrentThreadId(threadId);
        setError(null);
        resetAgentPlanMode();
        clearPlan();
        return;
      }

      const version = ++switchVersionRef.current;

      try {
        setCurrentThreadId(threadId);
        setError(null);

        resetAgentPlanMode();
        clearPlan();

        let threadMessages: ThreadMessage[] = [];
        try {
          const convertedMessages =
            await threadLoader.loadThreadMessages(
              projectId,
              threadId,
            );
          threadMessages = convertedMessages;
        } catch (backendError) {
          logger.error(
            'Failed to load thread messages:',
            backendError,
          );
        }

        // A newer switchToThread was fired while we were loading
        // — discard this result to avoid overwriting fresh data.
        if (switchVersionRef.current !== version) {
          logger.info(
            `[ThreadManager] switchToThread for ` +
              `${threadId.slice(-8)} superseded, ` +
              `skipping state update`,
          );
          return;
        }

        const seededMessages =
          recoveryMessageId &&
          !threadMessages.some((m) => m.id === recoveryMessageId)
            ? [
                ...threadMessages,
                {
                  id: recoveryMessageId,
                  threadId,
                  role: 'assistant' as const,
                  content: [],
                  status: 'streaming' as const,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                  metadata: {
                    custom: {
                      recoveryHint: true,
                    },
                  },
                } as ThreadMessage,
              ]
            : threadMessages;

        const streamingMsgIndex = seededMessages.findIndex(
          (m) => m.status === 'streaming',
        );
        const streamingMsg =
          streamingMsgIndex >= 0
            ? seededMessages[streamingMsgIndex]
            : null;
        logger.info(
          `[ThreadManager] DB loaded ` +
            `${threadMessages.length} msgs for ` +
            `thread=${threadId.slice(-8)}, ` +
            `streamingMsg=${
              streamingMsg
                ? `id=${streamingMsg.id.slice(-8)} ` +
                  `contentLen=` +
                  `${streamingMsg.content.length} ` +
                  `types=[${streamingMsg.content
                    .map((c) => c.type)
                    .join(',')}]`
                : 'none'
            }`,
        );

        let finalMessages = seededMessages;
        if (streamingMsg) {
          try {
            const state = await getMessageState(
              projectId,
              threadId,
              streamingMsg.id,
            );

            // Another switch happened during the Redis call
            if (switchVersionRef.current !== version) {
              return;
            }

            if (
              state.status !== 'not_found' &&
              state.content.length > 0
            ) {
              logger.info(
                `[ThreadManager] Redis state loaded ` +
                  `for ${streamingMsg.id.slice(-8)}: ` +
                  `${state.content.length} blocks, ` +
                  `status=${state.status}`,
              );
              const mergedMessages = [...seededMessages];
              mergedMessages[streamingMsgIndex] = {
                ...streamingMsg,
                content: state.content,
                status:
                  state.status === 'complete'
                    ? 'complete'
                    : streamingMsg.status,
              };
              finalMessages = mergedMessages;
            }
          } catch (stateError) {
            logger.warn(
              '[ThreadManager] Failed to fetch ' +
                'Redis state',
              stateError,
            );
          }
        }

        // Final guard before applying state
        if (switchVersionRef.current !== version) {
          return;
        }

        // Merge: replace only the target thread's messages
        // while preserving messages from other threads
        // (including any that are actively streaming).
        setMessages((prev) => [
          ...prev.filter((m) => m.threadId !== threadId),
          ...finalMessages,
        ]);

        // finalMessages, not threadMessages: when a streaming thread is
        // restored via recoveryMessageId we merge Redis content into
        // finalMessages above. Plan/todo extraction must see that recovered
        // content too, otherwise the side panels stay stale until the next
        // stream tick lands.
        extractAndSetPlan(finalMessages);

        if (todosContext) {
          todosContext.extractTodosFromMessages(
            finalMessages,
          );
        }

        setAvailableIds(new Set());
      } catch {
        setError('Failed to switch thread');
      }
    },
    [
      setCurrentThreadId,
      setError,
      setMessages,
      setAvailableIds,
      extractAndSetPlan,
      clearPlan,
      resetAgentPlanMode,
      todosContext,
      streamingThreadIds,
    ],
  );

  /**
   * Create a new thread
   *
   * Does NOT clear messages for other threads — background SSE
   * streams need their messages to remain in React state so that
   * updateMessage() can still find them by ID.
   */
  const createNewThread = useCallback(() => {
    setCurrentThreadId(null);
    setError(null);
    setAvailableIds(new Set());

    // Clear todos for new thread
    if (todosContext) {
      todosContext.clearTodos();
      todosContext.setIsTodoPanelOpen(false);
    }
  }, [setCurrentThreadId, setError, setAvailableIds, todosContext]);

  return {
    switchToThread,
    createNewThread,
  };
}
